import { AuthShellView, AuthStatusLine } from '@today/ui-domain'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
	LOGIN_CODE_EXCHANGE_FAILED,
	type LoginFailureRoute,
	loginFailureRoute,
	UNAUTHORIZED_PATH,
} from '@/auth/auth-errors'
import { FORCED_MFA_ENROLMENT_PATH } from '@/auth/forced-mfa-enrolment'
import { consumePkce } from '@/auth/pkce'
import { consumePostLoginRedirect } from '@/auth/post-login-redirect'
import { forwardSsoTestCallback } from '@/auth/sso-test-popup'
import { useAuthLogo } from '@/auth/useAuthLogo'
import { useAuth } from '@/hooks/useAuth'
import { AUTH_NAMESPACE } from '@/i18n'
import { authService } from '@/services/auth.service'

/**
 * OIDC callback landing. The Cognito hosted UI redirects here with `?code&state`. We validate
 * `state` against the stashed value (CSRF), exchange the code (+ PKCE verifier)
 * for our app session via the BFF, store the access token, then resume the
 * pre-login navigation target. Any failure routes to /error/unauthorized with a
 * `reason` of `LOGIN_CODE_EXCHANGE_FAILED` (REQ-006 / SCN-008) and stores no
 * token. A refusal the BFF names — including the TO-335 JIT codes — is carried
 * through as that code instead. An account refusal (`USER_NOT_PROVISIONED` /
 * `PRINCIPAL_INACTIVE`) routes to /error/no-access instead (TO-361 login-surface
 * REQ-005). Inside the SSO test-login popup (TO-308), the code is handed to the
 * opener instead of being exchanged here.
 */
export function AuthCallbackPage() {
	const [params] = useSearchParams()
	const navigate = useNavigate()
	const { refreshAuth } = useAuth()
	const { t } = useTranslation(AUTH_NAMESPACE)
	const logo = useAuthLogo()
	const [failure, setFailure] = useState<LoginFailureRoute | null>(null)
	// React 18 StrictMode double-invokes effects in dev; the code is single-use,
	// so guard against a duplicate exchange.
	const ran = useRef(false)

	useEffect(() => {
		if (ran.current) return
		ran.current = true

		// The SSO test-login popup (TO-308) hands its code to the opener, which has
		// the server verify it. It never starts a session in this window.
		if (forwardSsoTestCallback(params)) return

		const exchange = async () => {
			const code = params.get('code')
			const returnedState = params.get('state')
			const { verifier, state } = consumePkce()

			// No code, no CSRF-matching state, or no stashed PKCE verifier → the
			// exchange cannot legitimately be completed, so don't attempt it.
			if (!code || !returnedState || !state || returnedState !== state || !verifier) {
				setFailure({ path: UNAUTHORIZED_PATH, reason: LOGIN_CODE_EXCHANGE_FAILED })
				return
			}

			try {
				const { accessToken, mfaEnrolmentRequired } = await authService.exchangeCallback({
					code,
					codeVerifier: verifier,
				})
				localStorage.setItem('accessToken', accessToken)
				// Nudge the provider to re-read the now-stored token (its /auth/me
				// query is `enabled: hasToken`, re-evaluated only on re-render). Then
				// navigate: ProtectedRoute shows its loading spinner while /auth/me
				// fetches and renders the target once authenticated — instead of
				// seeing a stale `isAuthenticated === false` and bouncing to /login
				// (the "had to log in twice" race on first login).
				refreshAuth()
				// A required-but-unenrolled user goes to forced enrolment no matter how
				// they logged in (TO-612) — not only via the invitation first-login flow.
				// Without this, a normal /login by a pending super-admin lands on a
				// dead-end dashboard (no role yet, every admin surface refused).
				const target = mfaEnrolmentRequired ? FORCED_MFA_ENROLMENT_PATH : (consumePostLoginRedirect() ?? '/')
				void navigate(target, { replace: true })
			} catch (error) {
				setFailure(loginFailureRoute(error))
			}
		}

		void exchange()
	}, [params, navigate, refreshAuth])

	useEffect(() => {
		if (failure) void navigate(failure.path, { replace: true, state: { reason: failure.reason } })
	}, [failure, navigate])

	return (
		<AuthShellView logo={logo} title={t('login.title')}>
			<AuthStatusLine text={t('callback.signingIn')} />
		</AuthShellView>
	)
}
